import Box from "@mui/material/Box";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import MaterialReactTable from "material-react-table";
import {
  Button,
  CircularProgress,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMemo, useState } from "react";
import { formatMoney } from "../utils/formatMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const TablaSoloPendientes = ({
  data,
  totales,
  loading,
  onVolver,
  onAplicarRecibo,
  handleOpenInvoiceDetail,
  handleOpenReceiptDetail,
  handleAplicarRecibo,
}) => {
  const [copiedId, setCopiedId] = useState(null);

  const buildRowActions = (row, closeMenu) => {
    const actions = [];
    const comprobante = row.comprobante?.toUpperCase();
    const isRecibo = comprobante === "RECIBO";
    const isFactura = !isRecibo;

    if (isFactura) {
      actions.push(
        <MenuItem
          key="ver"
          onClick={() => {
            closeMenu();
            handleOpenInvoiceDetail(row);
          }}
        >
          <ListItemIcon>
            <PictureAsPdfIcon color="secondary" />
          </ListItemIcon>
          Ver saldo
        </MenuItem>,
      );
    }

    if (isRecibo) {
      actions.push(
        <MenuItem
          key="ver-recibo"
          onClick={() => {
            closeMenu();
            handleOpenReceiptDetail(row);
          }}
        >
          <ListItemIcon>
            <PictureAsPdfIcon color="primary" />
          </ListItemIcon>
          Ver recibo
        </MenuItem>,
      );
      actions.push(
        <MenuItem
          key="ver-recibo"
          disabled={row.saldoImputado === 0}
          onClick={() => {
            closeMenu();
            console.log("Recibo: ", row);
            handleAplicarRecibo(row);
          }}
        >
          <ListItemIcon>
            <AccountBalanceWalletIcon sx={{ color: "#631700 " }} />
          </ListItemIcon>
          Aplicar recibo
        </MenuItem>,
      );
    }

    return actions;
  };

  const estaVencidaConSaldoPendiente = (comprobante, fechaVencimiento) => {
    if (!fechaVencimiento) return false;
    if (String(comprobante).toLocaleLowerCase() !== "factura") return false;
    return new Date(fechaVencimiento).getTime() < Date.now();
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "primary",
    size: "small",
  };

  const bgColorTableHeadCell = "rgba(212, 230, 241, 0.8)";

  const displayColumnDefOptions = {
    "mrt-row-actions": {
      muiTableHeadCellProps: {
        sx: {
          fontSize: 14,
          color: "black",
          fontWeight: "normal",
          paddingTop: 1,
          paddingBottom: 1,
          backgroundColor: bgColorTableHeadCell,
          borderColor: "#FDFEFE",
        },
      },
      size: 60,
    },
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: {
      pageIndex: 0,
      pageSize: 100,
    },
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 14,
      color: "black",
      fontWeight: "normal",
      paddingTop: 1,
      paddingBottom: 1,
      backgroundColor: "rgba(235, 235, 235, 0.8)",
      borderColor: "#FDFEFE",
    },
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "fecha_emision",
        header: "Fecha",
        muiTableBodyCellProps: {
          align: "center",
        },
        maxSize: 50,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "-";

          // Tomás solo la parte de la fecha, sin convertir timezone
          const dateStr = value.toString().split("T")[0];
          const [year, month, day] = dateStr.split("-");
          return `${day}/${month}/${year}`;
        },
        Footer: () => <Typography fontWeight={800}>TOTALES</Typography>,
      },
      { accessorKey: "origen", header: "Tipo", maxSize: 50 },
      {
        accessorKey: "nro_comprobante",
        header: "Comprobante",
        maxSize: 50,
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          const isCopied = copiedId === row.original.id;

          const handleCopy = () => {
            navigator.clipboard.writeText(value);
            setCopiedId(row.original.id);
            setTimeout(() => setCopiedId(null), 2000);
          };

          return (
            <Box sx={{ display: "inline-block" }}>
              <Tooltip
                open={isCopied}
                title="¡Copiado!"
                placement="right"
                arrow
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <Typography
                  onClick={handleCopy}
                  sx={{
                    cursor: "pointer",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                    fontSize: "0.82rem",
                    fontWeight: 500,
                    letterSpacing: "0.03em",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "primary.main",
                    },
                  }}
                >
                  {value}
                </Typography>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        accessorKey: "fecha_vencimiento",
        header: "Vence el",
        muiTableBodyCellProps: {
          align: "center",
        },
        muiTableHeadCellProps: {
          align: "center",
        },
        maxSize: 50,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value || value === "0000-00-00") return "-";
          const dateStr = value.toString().split("T")[0];
          const [year, month, day] = dateStr.split("-");
          return `${day}/${month}/${year}`;
        },
      },
      {
        accessorKey: "debe",
        header: "Debe",
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableFooterCellProps: {
          align: "right",
        },
        maxSize: 50,
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          if (value === 0 || !value) return;

          return (
            <Typography
              sx={{
                color: "success.main",
                fontWeight: 600,
              }}
            >
              ${formatMoney(value)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography sx={{ color: "success.main", fontWeight: 700 }}>
            ${formatMoney(totales.debe)}
          </Typography>
        ),
      },
      {
        accessorKey: "haber",
        header: "Haber",
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableFooterCellProps: {
          align: "right",
        },
        maxSize: 50,
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          if (value === 0 || !value) return;
          return (
            <Typography
              sx={{
                color: "error.main",
                fontWeight: 600,
              }}
            >
              ${formatMoney(value)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography sx={{ color: "error.main", fontWeight: 700 }}>
            ${formatMoney(totales.haber)}
          </Typography>
        ),
      },
      {
        accessorKey: "saldo_cta_cte",
        header: "Saldo",
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableFooterCellProps: {
          align: "right",
        },
        maxSize: 50,
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          const color = value >= 0 ? "success.main" : "error.main";
          return (
            <Typography
              sx={{
                color: color,
                fontWeight: 600,
              }}
            >
              ${formatMoney(value)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography
            sx={{
              color: totales.saldo > 0 ? "success.main" : "error.main",
              fontWeight: 700,
            }}
          >
            ${formatMoney(totales.saldo)}
          </Typography>
        ),
      },
      {
        accessorKey: "nro_remito",
        header: "N° Remito",
        maxSize: 50,
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: {
          align: "right",
        },
      },
    ],
    [totales.debe, totales.haber, totales.saldo, copiedId],
  );

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "50vh",
          gap: 2,
        }}
      >
        <CircularProgress color="primary" size={48} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Cargando documentos pendientes...
        </Typography>
      </Box>
    );

  const today = new Date().getTime();

  return (
    <MaterialReactTable
      renderTopToolbarCustomActions={() => (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            onClick={onVolver}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: 3 }}
          >
            Volver a la cuenta corriente
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AccountBalanceWalletIcon />}
            onClick={onAplicarRecibo}
            sx={{ borderRadius: 3 }}
          >
            Aplicar Recibo
          </Button>
        </Box>
      )}
      muiTableBodyRowProps={({ row }) => {
        const { debe, haber } = row.original;

        // console.log("ROWDATA: ", row.original);
        let color = "inherit";

        if (debe) color = "success.main";
        if (haber) color = "error.main";

        return {
          sx: {
            "& td": {
              color,
              fontWeight: 600,
            },
          },
        };
      }}
      muiSearchTextFieldProps={muiSearchTextFieldProps}
      localization={MRT_Localization_ES}
      columns={columns}
      data={data}
      enableColumnFilterModes={false}
      enableSorting={false}
      enableColumnOrdering={false}
      enableGrouping={false}
      enableRowActions
      muiTableContainerProps={{
        sx: {
          maxHeight: "47vh",
        },
      }}
      enableStickyFooter
      enableStickyHeader
      initialState={initialState}
      enableRowSelection={false}
      positionActionsColumn="last"
      enableHiding={false}
      muiTableHeadCellProps={tableHeadCellProps}
      positionExpandColumn="first"
      enableExpandAll={false}
      muiTableFooterCellProps={{
        sx: {
          backgroundColor: bgColorTableHeadCell,
          borderColor: "#FDFEFE",
        },
      }}
      muiTableBodyCellProps={({ row }) => ({
        align: "left",

        sx: {
          backgroundColor: estaVencidaConSaldoPendiente(
            row.original.comprobante,
            row.original.fecha_vencimiento,
          )
            ? row.index % 2 === 0
              ? "#FEF3C7"
              : "#ffeea8"
            : row.index % 2 === 0
              ? "#ffffff"
              : "#fafafa",
          color: "black",
          borderBottom: "1px solid rgba(224,224,224,1)",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        },
        hover: true,
      })}
      enableColumnActions={false}
      enableColumnFilters={false}
      displayColumnDefOptions={displayColumnDefOptions}
      enableDensityToggle={false}
      renderRowActionMenuItems={({ closeMenu, row }) =>
        buildRowActions(row.original, closeMenu)
      }
    />
  );
};

export default TablaSoloPendientes;
